import * as React from "react";
import type { AriaRole } from "react";
import { cva } from "class-variance-authority";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { cn } from "../lib/utils";
import { Button } from "../ui-custom/button";
import { IconButton } from "./icon-button";
import { Spinner } from "./spinner";

export type NoticeTone = "info" | "warning" | "error" | "success";
export type NoticeDensity = "standard" | "compact";
export type NoticePlacement = "section" | "inline" | "embedded";

export interface NoticeAction {
  label: string;
  onClick: () => void;
  /** `primary` renders as an outline button, `secondary` as ghost. Unset: first action primary, second secondary. */
  variant?: "primary" | "secondary";
  disabled?: boolean;
  loading?: boolean;
  "data-testid"?: string;
}

type NoticeRole = Extract<AriaRole, "status" | "alert" | "note">;

type NoticeActionPlacementProps =
  | {
      placement?: Exclude<NoticePlacement, "embedded">;
      /** Up to two actions. Extra entries are dropped. */
      actions?: NoticeAction[];
    }
  | {
      /** Embedded notices sit beneath a field and never carry actions. */
      placement: "embedded";
      actions?: never;
    };

type NoticeDismissProps =
  | {
      dismissible?: false;
      onDismiss?: never;
    }
  | {
      dismissible: true;
      onDismiss: () => void;
    };

type NoticeBaseProps = {
  tone?: NoticeTone;
  density?: NoticeDensity;
  title?: React.ReactNode;
  children: React.ReactNode;
  /** Replaces the tone's default Lucide icon. Always rendered `aria-hidden`. */
  icon?: LucideIcon;
  /** Defaults per tone: info/success → `status` (announced politely), warning/error → `note`. */
  role?: NoticeRole;
  /** Screen-reader label for the dismiss button. Apps localize it; the default
   * keeps every existing app's output identical. */
  dismissLabel?: string;
  className?: string;
  ref?: React.Ref<HTMLDivElement>;
  "data-testid"?: string;
};

export type NoticeProps = NoticeDismissProps & NoticeActionPlacementProps & NoticeBaseProps;

const TONE_ICONS: Record<NoticeTone, LucideIcon> = {
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
  success: CheckCircle2,
};

const TONE_DEFAULT_ROLE: Record<NoticeTone, NoticeRole> = {
  info: "status",
  warning: "note",
  error: "note",
  success: "status",
};

// Rebel's `--color-<tone>-icon` → the semantic token itself. Error uses the
// `destructive` family, exactly as Rebel does.
const TONE_ICON_CLASS: Record<NoticeTone, string> = {
  info: "text-info",
  warning: "text-warning",
  error: "text-destructive",
  success: "text-success",
};

// Rebel's `--color-<tone>-surface` / `-border` → opacity modifiers on the
// semantic token (`/10` + `/30`); the `-strong` pair used by `section` →
// `/15` + `/40`. Light and dark both fall out of the token, so no theme
// branches are needed here.
const noticeVariants = cva(
  "relative grid w-full grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-3 overflow-hidden rounded-md border px-4 py-3 text-foreground shadow-sm transition-colors data-[has-title=true]:items-start",
  {
    variants: {
      tone: {
        info: "border-info/30 bg-info/10",
        warning: "border-warning/30 bg-warning/10",
        error: "border-destructive/30 bg-destructive/10",
        success: "border-success/30 bg-success/10",
      },
      placement: {
        section: "rounded-md",
        inline: "rounded-md",
        embedded: "gap-2 rounded-sm",
      },
      density: {
        standard: "",
        compact: "gap-2 px-3 py-2",
      },
    },
    compoundVariants: [
      { tone: "info", placement: "section", className: "border-info/40 bg-info/15" },
      { tone: "warning", placement: "section", className: "border-warning/40 bg-warning/15" },
      { tone: "error", placement: "section", className: "border-destructive/40 bg-destructive/15" },
      { tone: "success", placement: "section", className: "border-success/40 bg-success/15" },
    ],
    defaultVariants: {
      tone: "info",
      placement: "inline",
      density: "standard",
    },
  },
);

/**
 * Notice is the persistent in-flow status, attention, and prerequisite
 * primitive — the sister of `toast`, which is transient. Use it for messages
 * that should stay visible near the thing they affect: permission warnings,
 * prerequisite nudges, connection results, field-embedded warnings. The three
 * placements (`section`, `inline`, `embedded`) render at deliberately different
 * weights so one primitive can serve all three roles without flattening them.
 */
function Notice({
  tone = "info",
  density = "standard",
  placement = "inline",
  title,
  children,
  icon: IconOverride,
  actions,
  role,
  dismissible,
  onDismiss,
  dismissLabel = "Dismiss notice",
  className,
  ref,
  "data-testid": dataTestId,
}: NoticeProps) {
  const Icon = IconOverride ?? TONE_ICONS[tone];
  const resolvedRole: NoticeRole = role ?? TONE_DEFAULT_ROLE[tone];
  const ariaLive = resolvedRole === "status" ? "polite" : undefined;
  const limitedActions = actions ? actions.slice(0, 2) : undefined;
  const actionCount = limitedActions?.length ?? 0;
  const isCompact = density === "compact";
  const isEmbedded = placement === "embedded";
  const iconSize = isCompact || isEmbedded ? 16 : 18;
  const hasTitle = Boolean(title);

  return (
    <div
      ref={ref}
      data-slot="notice"
      role={resolvedRole}
      aria-live={ariaLive}
      data-tone={tone}
      data-placement={placement}
      data-density={density}
      data-has-title={hasTitle ? "true" : "false"}
      data-action-count={actionCount}
      data-testid={dataTestId}
      className={cn(noticeVariants({ tone, placement, density }), className)}
    >
      <span
        data-slot="notice-icon"
        aria-hidden="true"
        className={cn(
          "relative z-[1] inline-flex items-center justify-center leading-none [&_svg]:block",
          TONE_ICON_CLASS[tone],
          hasTitle && (isCompact ? "mt-[0.1125rem]" : "mt-[0.05rem]"),
        )}
      >
        <Icon size={iconSize} />
      </span>

      <div data-slot="notice-body" className="relative z-[1] flex min-w-0 flex-auto flex-col gap-1">
        {title ? (
          <p data-slot="notice-title" className="m-0 text-sm font-semibold leading-[1.4] text-foreground">
            {title}
          </p>
        ) : null}
        <div
          data-slot="notice-description"
          className={cn(
            "m-0 text-sm leading-normal text-muted-foreground [&>:first-child]:mt-0 [&>:last-child]:mb-0 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-[0.12em] [&_ul]:mt-2 [&_ul]:pl-5",
            (isCompact || isEmbedded) && "text-[0.8125rem]",
          )}
        >
          {children}
        </div>
      </div>

      {limitedActions && actionCount > 0 ? (
        <div
          data-slot="notice-actions"
          className={cn(
            "relative z-[1] flex max-w-full flex-wrap items-center justify-end gap-2 self-center",
            actionCount === 2 && "flex-col items-stretch",
          )}
        >
          {limitedActions.map((action, index) => {
            const buttonVariant =
              action.variant === "secondary" || (action.variant === undefined && index === 1)
                ? "ghost"
                : "outline";
            return (
              <Button
                key={index}
                type="button"
                size="sm"
                variant={buttonVariant}
                onClick={action.onClick}
                disabled={action.disabled || action.loading}
                data-testid={action["data-testid"]}
              >
                {action.loading ? <Spinner size="sm" /> : null}
                {action.label}
              </Button>
            );
          })}
        </div>
      ) : null}

      {dismissible ? (
        <IconButton
          type="button"
          size="xs"
          variant="ghost"
          aria-label={dismissLabel}
          onClick={onDismiss}
          data-slot="notice-dismiss"
          className="relative z-[1] ml-1 self-start"
        >
          <X size={14} />
        </IconButton>
      ) : null}
    </div>
  );
}

export { Notice, noticeVariants };
